import { useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Heading,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from 'components/v4';
import {
    Box,
    ModalCloseButton,
    Stack,
    Checkbox,
    FormControl,
    FormLabel,
    VStack,
} from '@chakra-ui/react';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import { billingDayDarkMode } from '@/constants';
import { useSelector } from 'react-redux';
import { axios } from '../../../../services/axios';

const useChangeBillingDay = ({
    handleCloseModal,
    openModal,
    currentBillingDay,
    companyID,
    handleRefreshModal,
    partnerID,
    wholesalerID,
    mode,
}) => {
    const [date, setDate] = useState(currentBillingDay?.toString() || '0');
    const [tncChecked, setTNCChecked] = useState(false);
    const [error, setError] = useState(null);

    const handleChangeDate = (val) => {
        setDate(val);
    };

    const handleChangeTNC = (e) => {
        setTNCChecked(e.target.checked);
    };

    const handleSubmit = async () => {
        setError(null);
        try {
            const payload = {
                newBillingDay: parseInt(date),
            };

            if (mode === 'Wholesaler') {
                payload.wholesalerID = wholesalerID;
            } else if (mode === 'Partner') {
                payload.partnerID = partnerID;
            } else {
                payload.companyID = companyID;
            }

            const response = await axios.post(
                `/${mode}/UpdateBillingDay`,
                payload,
            );

            handleCloseModal();
            handleRefreshModal && handleRefreshModal(response.data);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || e?.message;

            // For Partner mode, if API fails (e.g., no accumulating invoice), allow direct update
            if (
                mode === 'Partner' &&
                errorMessage?.includes('Accumulating Invoice')
            ) {
                handleCloseModal();
                // Pass the date value directly as second parameter
                handleRefreshModal && handleRefreshModal(null, parseInt(date));
            } else {
                setError(
                    `Could not update billing day at this time.\n${errorMessage}\nStatus Code: ${e?.response?.status}\n`,
                );
            }
        }
    };

    const warningText = [
        `By changing a ${mode?.toLowerCase()} billing date you are setting the ${mode?.toLowerCase()} invoice generation date to the new date selected.`,
        'This does not take into account charges from the existing period already allocated to the invoice.',
        'You may need to apply a manual adjustment to the last invoice in order to align these new charges.',
        'All future invoices will have a period starting from the selected date above.',
    ].join('\n\n');

    const warningTitle = 'Warning';
    const termsAndConditionsTitle = 'Agreement';
    const termsAndConditions = `I understand that I am changing the billing date on behalf of this ${mode?.toLowerCase()}.`;
    const modalTitle = 'Change Billing Date';

    return {
        handleCloseModal,
        handleSubmit,
        openModal,
        warningText,
        warningTitle,
        handleChangeDate,
        date,
        termsAndConditions,
        termsAndConditionsTitle,
        handleChangeTNC,
        tncChecked,
        modalTitle,
        fieldName: 'Billing Date',
        error,
    };
};

export default function ChangeBillingDay(props) {
    const { billingDayDropdown } = props;
    const {
        handleCloseModal,
        handleSubmit,
        openModal,
        warningText,
        warningTitle,
        handleChangeDate,
        date,
        termsAndConditions,
        termsAndConditionsTitle,
        handleChangeTNC,
        tncChecked,
        fieldName,
        modalTitle,
        error,
    } = useChangeBillingDay(props);

    const { darkMode } = useSelector((state) => state.settings);

    return (
        <Modal isOpen={openModal} onClose={handleCloseModal} isCentered>
            <ModalOverlay />
            <ModalContent width="600px">
                <ModalHeader>{modalTitle}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Box
                        className="change-billing-date"
                        sx={
                            darkMode
                                ? billingDayDarkMode
                                : {
                                      '& .chakra-select__wrapper': {
                                          width: 50,
                                          textAlign: 'center',
                                      },
                                  }
                        }>
                        <Stack spacing={4}>
                            {error && (
                                <Alert status="error" whiteSpace="pre-line">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            <FormControl>
                                <FormLabel>{fieldName}</FormLabel>
                                <DropDown
                                    data={billingDayDropdown}
                                    shownValue="label"
                                    selectedId="value"
                                    currentId={date}
                                    handleSelectChange={(item) =>
                                        handleChangeDate(
                                            item?.id ?? item?.value,
                                        )
                                    }
                                    placeholder=""
                                    width="100%"
                                    isLoading={false}
                                    darkMode={darkMode}
                                />
                            </FormControl>
                            <Alert status="warning" whiteSpace="pre-line">
                                <AlertIcon />
                                <AlertDescription>
                                    <Heading as="h6" size="xs" mb={2}>
                                        {warningTitle}
                                    </Heading>
                                    <Text whiteSpace="pre-line">
                                        {warningText}
                                    </Text>
                                </AlertDescription>
                            </Alert>
                            <Alert status="info">
                                <VStack alignItems="center" marginRight={3}>
                                    <AlertIcon margin={0} />
                                    <Checkbox
                                        isChecked={tncChecked}
                                        onChange={handleChangeTNC}
                                        className="agreement-checkbox"
                                    />
                                </VStack>
                                <AlertDescription>
                                    <Heading as="h6" size="xs" mb={2}>
                                        {termsAndConditionsTitle}
                                    </Heading>
                                    <Text whiteSpace="pre-line">
                                        {termsAndConditions}
                                    </Text>
                                </AlertDescription>
                            </Alert>
                        </Stack>
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" mr={3} onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="brand"
                        onClick={handleSubmit}
                        isDisabled={!tncChecked}>
                        Submit
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
